<template>
  <SubLayout
      :pageTitle="title"
      :pageBreadcrumbs="breadcrumbs"
      :hasBack="false"
  >
    <template #layoutContent>
      <Loader :is-loading="isLoading">
        <ActionsBar :query="query" :handleRangeDate="handleRangeDate" :contacts="contacts" :status="invoiceStatus" id="invoice-datepicker"/>
        <Card :title="$t('recurrent.list')" containerClass="mb-5 mt-5">
          <template #cardToolbar>
            <div class="d-flex justify-content-end align-items-center">
              <div>
                <Button type="new" v-if="$can(abilities.CREATE_RECURRING_INVOICE)" btn-class="btn-sm me-2" btn-text="recurrent.create" @click="openCreateRecurrentView" ></Button>
              </div>
            </div>
          </template>
          <template #cardBody>
            <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4"
                    id="recurring-invoices_datatable">
                <thead class="mb-5">
                  <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                    <th class="min-w-100px text-center">{{ $t('invoice.label.date') }}</th>
                    <th class="min-w-100px text-center">{{ $t('invoice.label.recurrent.next_recurring_date') }}</th>
                    <th class="min-w-100px text-center">{{ $t('invoice.label.customer') }}</th>
                    <th class="min-w-100px text-center">{{ $t('invoice.label.status') }} </th>
                    <th class="min-w-100px text-center">{{ $t('invoice.label.totalAmount') }}</th>
                    <th class="w-100px text-start">{{ $t('label.action') }}</th>
                  </tr>
                </thead>
            </table>
          </template>
        </Card>
      </Loader>
    </template>
  </SubLayout>
</template>

<script setup>

import {computed, nextTick, onMounted, ref, watch} from "vue";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import {useStore} from "vuex";
import i18n from "@/plugins/i18n";
import Ls from "@/services/ls";
import {BASE_URL} from "@/services/api";
import {formattedLocalAmount} from "@/mixins/Helpers";
import {useRouter} from "vue-router";
import {useLocales} from "@/composables/locales";
import {useHasAbility} from "@/composables/hasAbility";
import ActionsBar from "@/views/Invoice/components/ActionsBar.vue";
import Card from "@/Modules/CRM/components/Card";
import Button from "@/components/Button";
import abilities from "@/composables/abilities";
import {Theme} from "@/composables/theming";
import Loader from "@/components/Loader.vue"

const router = useRouter()
const store = useStore()
const { can } = useHasAbility()
const {locale, datatableLocaleUrl} = useLocales()

const title = ref("breadcrumbs.recurrent.title")
const breadcrumbs = ref([
  {
    name: 'breadcrumbs.recurrent.list',
    route: 'invoices/recurrent'
  }
])

const table = ref()
const query = ref({
  status: 'all',
  contact: 'all',
  start_date: '',
  end_date: ''
})
const isLoading = ref(true)

const invoiceStatus = computed(() => store.getters['invoices/recurringInvoiceStatus'])
const contacts = computed(() => store.getters['contacts/getContacts'])

watch(() => locale.value, (value) => {
  nextTick(() => fetchDatas() )
}, { deep: true })

watch(() => isLoading.value, (value) => {
  if(!value){
    nextTick(() => fetchDatas())
  }
}, { deep: true })

watch(() => query.value.status, (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/recurring/invoices/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})

watch(() => query.value.contact, (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/recurring/invoices/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})


watch(() => [query.value.start_date, query.value.end_date], (newValue) => {
  table.value.ajax.url(`${BASE_URL}/get/recurring/invoices/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})

function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#recurring-invoices_datatable").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 0, 'desc']],
    ajax: {
      url: BASE_URL + `/get/recurring/invoices/datatables?status=${query.value.status}&contact=${query.value.contact}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`,
      type: "GET",
      beforeSend: function (request) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [
    { data: "date" }, { data: "next_recurring_at" }, { data: "contact" }, { data: "status" }, { data: "total" }, { data: "Actions", responsivePriority: -1 }
    ],
    columnDefs: [


      {
        targets: -5,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },

      {
        targets: -4,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },

      {
        targets: -3,
        orderable: false,
        className: "dt-center text-center" ,
        render: function (t, a, e, l) {
          let status = getInvoiceStatusName(t)
          const locale = i18n.global.locale.value
          let color = status.color
          status = status[locale]
          return '<span class="badge badge-light-' + color +'" style="font-size:13px; margin:3px; padding:5px"><span style="margin-left: 5px" class="valueRow' + l.row + ' label label-success label-inline font-weight-bolder mr-2 edit_value_btn">' + status + '</span></span>'
        },
      },

      {
        targets: -2,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + formattedLocalAmount(t) + ' ' + e.currency + "</span>";
        },
      },
      {
        width: "10%",
        targets: -1,
        "className": "dt-center text-start",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render=""
          if(can(abilities.VIEW_RECURRING_INVOICE) || can(abilities.VIEW_ALL_RECURRING_INVOICE)){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon show_btn" title="'+ i18n.global.t('button.show') +'"><i  data-id="'+ e.id +'" class="fas fa-eye fs-2" style="color: '+Theme.gray +'"></i></a>'
          }

          if(can(abilities.UPDATE_RECURRING_INVOICE) || can(abilities.UPDATE_ALL_RECURRING_INVOICE)){
            if(e.status !== 'completed'){
              render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon edit_btn" title="'+ i18n.global.t('button.edit') +'"><i  data-id="'+ e.id +'" class="fas fa-edit fs-2"  style="color: '+Theme.gray +'"></i></a>'
            }
          }
          if(false){
            render += '<a href="javascript:;" data-row="'+ l.row +'" data-id="'+ e.id +'" class="btn btn-sm btn-clean btn-icon delete_btn" title="'+ i18n.global.t('button.delete') +'"><i data-row="'+ l.row +'" data-id="'+ e.id +'" class="fas fa-trash fs-2" style="color: '+Theme.gray +'"></i></a>'
          }

          return render
        },
      },

    ],
    language: {
      url: datatableLocaleUrl.value,
    }
  });

  $(document).on("click", "#recurring-invoices_datatable .show_btn", (e) => {
    router.push({ name: 'recurrent-invoices-view', params: { id: e.target.getAttribute('data-id') } })
  });

  $(document).on("click", "#recurring-invoices_datatable .edit_btn", (e) => {
    router.push({ name: 'recurrent-invoices-edit', params: { id: e.target.getAttribute('data-id') } })
  });

}

function getInvoiceStatusName(status) {
  const {color, fr, en} = invoiceStatus.value.find(el => el.value === status) || ''
  return {color, fr, en}
}

function loadData() {
  Promise.all([
    store.dispatch('invoices/loadRecurrentInvoiceStatus'),
    store.dispatch('contacts/loadContacts'),
    fetchDatas()
  ]).finally(() => {
    isLoading.value = false
  })
}

onMounted( () => {
  loadData()
})

function handleRangeDate (range) {
  query.value.start_date = range.start_date
  query.value.end_date = range.end_date
}

function openCreateRecurrentView(){
  router.push({ name: 'recurrent-invoices-create' })
}
</script>

<style scoped>

</style>
